import { Box } from '@mui/material';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { HEADER_DROPDOWN_MENU_ID, HEADER_HEIGHT, PROFILE_MENU_ID } from '../../constants/layout';
import { EVENEMENTS, PROFIL } from '../../constants/routing';
import { useViewport } from '../../hooks/useViewport';
import { ReactLink } from '../../models/common';
import { StrapiEnTeteEntity } from '../../models/layout';
import theme from '../../theme';
import { transformHeaderLinks } from '../../utils/layout';
import { localizeUrl } from '../../utils/tools';
import { IconButton } from '../atoms/IconButton';
import { Image } from '../atoms/Image';
import { LanguageSelector } from '../atoms/LanguageSelector';
import { Link } from '../atoms/Link';
import { SkipLink } from '../atoms/SkipLink';
import { IconBurgerMenu } from '../icons/IconBurgerMenu';
import { IconUser } from '../icons/IconUser';
import { DropdownMenu } from '../molecules/header/DropdownMenu';
import { HeaderLinksDesktop } from '../molecules/header/HeaderLinksDesktop';
import { LoggedOutButtons } from '../molecules/header/LoggedOutButtons';
import { MainBurgerMenu } from '../molecules/header/MainBurgerMenu';
import { UserBurgerMenu } from '../molecules/header/UserBurgerMenu';
import { Container } from './Container';

export type HeaderProps = {
  enTete?: StrapiEnTeteEntity;
  locale: string;
  isLoggedIn?: boolean;
  isAgendaAuthorized?: boolean;
};

// Added <Suspense> around components declarations to fix lazy loading on window resize
const Drawer = lazy(() => import('@mui/material/Drawer'));
const DrawerRight = lazy(() => import('../atoms/DrawerRight'));

export const Header = ({ enTete, locale, isLoggedIn, isAgendaAuthorized }: HeaderProps) => {
  const { t } = useTranslation();
  const { isSmallViewport } = useViewport();
  const [isMainBurgerMenuOpen, setIsMainBurgerMenuOpen] = useState(false);
  const [isUserBurgerMenuOpen, setIsUserBurgerMenuOpen] = useState(false);

  const location = useLocation();

  // Vérifie si l'icône de profil est sur la page actuelle
  const profilLink = { to: `/${PROFIL}/profil` };
  const isCurrentPageProfil = location.pathname.includes(`${PROFIL}/profil`);

  const [dropdownLinks, setDropdownLinks] = useState<ReactLink[]>([]);
  const [dropdownIndex, setDropdownIndex] = useState<number | undefined>();

  const handleDropdown = (sublinks: ReactLink[], index?: number) => {
    setDropdownLinks(sublinks);
    setDropdownIndex(index);
  };

  useEffect(() => {
    if (dropdownLinks.length !== 0) {
      //Focus first link of submenu when we open it (for accessibility)
      document.getElementById(HEADER_DROPDOWN_MENU_ID)?.getElementsByTagName('a')[0].focus();
    }
  }, [dropdownLinks]);

  if (!enTete) return null;
  const allHeaderLinks = transformHeaderLinks(enTete.LienMenus ?? []);
  const headerLinks =
    isAgendaAuthorized === false ? allHeaderLinks.filter((link) => !link.to?.includes(EVENEMENTS)) : allHeaderLinks;

  const isAnyDropdownActive = () => {
    return dropdownLinks.length > 0 || dropdownIndex !== undefined;
  };

  const ariaLabelMenuDisplay = t('menu.display');
  return (
    <Box
      component="header"
      sx={{
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        height: HEADER_HEIGHT,
        width: '100%',
        backgroundColor: '#fff'
      }}
    >
      {/* Desktop Header */}
      {!isSmallViewport && (
        <>
          <Box
            sx={{
              zIndex: 1000,
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              backgroundColor: '#fff'
            }}
          >
            <Container>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {/* Liens d'évitement RGAA */}
                <SkipLink />
                {isLoggedIn && isCurrentPageProfil && (
                  <SkipLink href={`#${PROFILE_MENU_ID}`} labelKey="accessibility.skipToProfileMenu" />
                )}

                {/* Logo */}
                <Box
                  sx={{
                    flex: '0 0 7rem',
                    width: '7rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Link
                    to={localizeUrl('/', isLoggedIn)}
                    sx={{
                      display: 'inline-block',
                      width: '100%',
                      lineHeight: 0
                    }}
                  >
                    <Image
                      src="/images/logo.png"
                      alt={t('layout.toHomepage') ?? 'logo'}
                      sx={{ height: 'auto', width: '100%' }}
                    />
                  </Link>
                </Box>

                {/* Links */}
                <Box
                  component="nav"
                  role="navigation"
                  sx={{
                    flex: '0 1 65rem',
                    px: '3rem',
                    pr: '3rem',
                    mx: 'auto'
                  }}
                >
                  <HeaderLinksDesktop
                    links={headerLinks}
                    dropdownVisible={isAnyDropdownActive()}
                    setDropdown={(sublinks, index) => {
                      // If there are sublinks, and selected submenu index is not the same
                      // Display sublinks
                      if (sublinks.length !== 0 && index !== dropdownIndex) {
                        handleDropdown(sublinks, index);
                      }
                      // Else, empty dropdown if not already empty
                      else if (dropdownLinks.length !== 0) {
                        handleDropdown([]);
                      }
                    }}
                  />
                </Box>

                {/* Lang, SignUp, SignIn */}
                <Box
                  sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '2rem'
                  }}
                >
                  <LanguageSelector locale={locale} />
                  <Box sx={{ display: 'flex', columnGap: '1rem' }}>
                    {isLoggedIn ? (
                      <>
                        {/* <SearchField /> */}
                        <IconButton
                          iconButtonType="light"
                          disableRipple
                          to={localizeUrl(profilLink.to, true)}
                          sx={{
                            border: isCurrentPageProfil ? `1px solid ${theme.palette.primary.main}` : 'none',
                            '&:focus, &:focus-visible': {
                              outline: `3px solid ${theme.palette.primary.main}`,
                              outlineOffset: '1px',
                              boxShadow: 'none'
                            }
                          }}
                        >
                          <IconUser alt={t('header.link.profile')} />
                        </IconButton>
                      </>
                    ) : (
                      <LoggedOutButtons />
                    )}
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>
          {isAnyDropdownActive() && <DropdownMenu dropdownLinks={dropdownLinks} onClick={() => handleDropdown([])} />}
        </>
      )}

      {/* Mobile Header */}
      {isSmallViewport && (
        <Container>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItem: 'center',
              my: '1.5rem'
            }}
          >
            {/* Liens d'évitement RGAA */}
            <SkipLink />
            {isLoggedIn && isCurrentPageProfil && (
              <SkipLink href={`#${PROFILE_MENU_ID}`} labelKey="accessibility.skipToProfileMenu" />
            )}

            {/* Main burger menu */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <IconButton
                aria-label={ariaLabelMenuDisplay}
                disableRipple
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: '-0.75rem',
                  '&:focus, &:focus-visible': {
                    backgroundColor: theme.palette.secondary.main,
                    boxShadow: 'none',
                    svg: {
                      line: {
                        stroke: 'white'
                      }
                    },
                    transition: 'none !important'
                  }
                }}
                onClick={() => {
                  setIsMainBurgerMenuOpen(true);
                }}
              >
                <IconBurgerMenu />
              </IconButton>
              <Suspense>
                <Drawer
                  PaperProps={{
                    sx: {
                      width: '100%'
                    }
                  }}
                  anchor="left"
                  open={isMainBurgerMenuOpen}
                >
                  <MainBurgerMenu
                    headerLinks={headerLinks}
                    isLoggedIn={isLoggedIn}
                    onMenuClose={() => {
                      setIsMainBurgerMenuOpen(false);
                    }}
                    locale={locale}
                  />
                </Drawer>
              </Suspense>
            </Box>

            {/* Logo */}
            <Box
              sx={{
                flex: '0 1 7rem',
                maxWidth: '7rem',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Link to={localizeUrl('/', isLoggedIn)} sx={{ display: 'inline-block', lineHeight: 0 }}>
                <Image src="/images/logo.png" alt={t('layout.toHomepage') ?? 'logo'} sx={{ height: '4rem' }} />
              </Link>
            </Box>

            {/* User menu */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              {isLoggedIn && (
                <>
                  <IconButton
                    aria-label={ariaLabelMenuDisplay}
                    iconButtonType="light"
                    disableRipple
                    onClick={() => {
                      setIsUserBurgerMenuOpen(true);
                    }}
                    sx={{
                      '&:focus, &:focus-visible': {
                        outline: `3px solid ${theme.palette.primary.main}`,
                        outlineOffset: '1px',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    <IconUser alt={t('header.link.profile')} />
                  </IconButton>
                  <Suspense>
                    <DrawerRight
                      open={isUserBurgerMenuOpen}
                      onClose={() => {
                        setIsUserBurgerMenuOpen(false);
                      }}
                    >
                      <UserBurgerMenu
                        onMenuClose={() => {
                          setIsUserBurgerMenuOpen(false);
                        }}
                      />
                    </DrawerRight>
                  </Suspense>
                </>
              )}
            </Box>
          </Box>
        </Container>
      )}
    </Box>
  );
};
